import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { marker } from '@colsen1991/ngx-translate-extract-marker';

import type { Observable, Subscription } from 'rxjs';
import { map, of } from 'rxjs';
import { AppStateService } from 'src/app/shared/services/app-state.service';
import { TokenStorageService } from 'src/app/shared/services/token-storage.service';
import { UserService } from 'src/app/shared/services/user.service';
import { EntityItemInterface } from 'src/app/shared/components/search-input/select-item.interface';
import { IDialogContentComponent } from 'src/app/shared/components/modal-dialog/modal-dialog.service';
import { ClinicTypeService } from 'src/app/shared/services/clinic-type.service';
import { ClinicTypeInterface } from 'src/app/shared/models/clinic-type.interface';
import { UserInterface } from '../../../../../models/user.interface';

@Component({
  selector: 'app-share-by-email',
  templateUrl: './share-by-email.component.html',
  styleUrls: ['./share-by-email.component.scss']
})
export class ShareByEmailComponent implements OnInit, OnDestroy, IDialogContentComponent<ShareStudyByEmailPopupResult, undefined> {
  onFilterUsers: (args: string) => Observable<EntityItemInterface[]>;

  selectedUsers: EntityItemInterface[] = [];
  newUsers: UserWithRoleInterface[] = [];

  isAddingNew = false;
  isValidEmail = false;

  email?: string;
  input: undefined;

  firstNameFormControl: FormControl = new FormControl('', [Validators.required]);
  lastNameFormControl: FormControl = new FormControl('', [Validators.required]);
  profilFormControl: FormControl = new FormControl('', [Validators.required]);
  clinicTypeFormControl: FormControl = new FormControl('', [Validators.required]);

  profiles: string[] = [ 'CLIENT', 'INVITED_VET', 'INVITED_CLINIC'];
  isUserFound = false;
  clinicTypes$: Observable< ClinicTypeInterface[]>;

  private profilSubscription?: Subscription;

  constructor(
    private readonly userService: UserService,
    private readonly appStateService: AppStateService,
    private readonly tokenStorageService: TokenStorageService,
    private readonly clinicTypeService: ClinicTypeService
  ) { }

  getResult(): ShareStudyByEmailPopupResult {
    return {
      newUsers : this.newUsers,
      userIds : this.selectedUsers.map(u => u.id),
      referingVetIds: []
    };
  }

  isDisabled(): boolean {
    return this.isAddingNew || (this.selectedUsers.length === 0 && this.newUsers.length === 0);
  }

  ngOnInit(): void {
    this.setTrad();
    this.setupProfilValidation();

    this.onFilterUsers = (value: string ) => {
      this.isUserFound = false;
      if (this.appStateService.clinicId) {
        return this.userService.searchExternalUsers(value.trim(), this.appStateService.clinicId)
          .pipe(map(items => items
            .map(item => {
              if (item.email.toUpperCase() === value.toUpperCase()) {
                this.isUserFound = true;
              }

              return { id: item.id, label : item.email + ' (' + item.firstName + ' ' + item.lastName + ')' };
            })));
      } else {
        return of([]);
      }
    };

    this.clinicTypes$ = this.clinicTypeService.getClinicTypes().pipe(map(clinicTypes => clinicTypes.filter(clinicType=> clinicType.shortLabel !== 'GRP' )));
  }

  ngOnDestroy(): void {
    this.profilSubscription?.unsubscribe();
  }

  onSelectedUserChanged(user: EntityItemInterface) {
    const userFound = this.selectedUsers.find(u=> u.id === user.id );
    if (!userFound) {
      this.selectedUsers.push(user);
    }
  }

  onValueChanged(text: string | UserInterface) {
    if (typeof text === 'string') {
      this.email = text.trim();
    }
  }

  onValidityChanged(isValid: boolean) {
    this.isValidEmail = isValid;
  }

  addNew() {
    this.isAddingNew = true;
  }

  isInvalidNewUser() {
    return (this.profilFormControl.invalid
      || (this.profilFormControl.value !== 'INVITED_CLINIC' && (this.lastNameFormControl.invalid || this.firstNameFormControl.invalid))
      || (this.profilFormControl.value === 'INVITED_CLINIC' && (this.lastNameFormControl.invalid || this.clinicTypeFormControl.invalid))
    );
  }

  ok() {
    this.isAddingNew = false;
    const userFound = this.newUsers.find(u=> u.email === this.email );
    if (!userFound && this.email) {
      // Pour les profils CLIENT, remplacer les champs vides par 'UNKNOWN'
      const firstName = this.profilFormControl.value !== 'INVITED_CLINIC' ?
        (this.profilFormControl.value === 'CLIENT' && !this.firstNameFormControl.value ? 'UNKNOWN' : this.firstNameFormControl.value) :
        this.clinicTypeFormControl.value.label;

      const lastName = this.profilFormControl.value === 'CLIENT' && !this.lastNameFormControl.value ?
        'UNKNOWN' :
        this.lastNameFormControl.value;

      this.newUsers.push({
        email : this.email,
        firstName,
        lastName,
        role : this.profilFormControl.value,
      });
      this.resetFields();
    }
  }

  deleteUserById(id: number) {
    this.selectedUsers = this.selectedUsers.filter(u=>u.id !== id);
  }

  deleteUserByEmail(email: string) {
    this.newUsers = this.newUsers.filter(u=>u.email !== email);
  }

  cancel() {
    this.isAddingNew = false;
    this.resetFields();
  }

  getEmailValidators() {
    return [Validators.email];
  }

  private setupProfilValidation(): void {
    this.profilSubscription = this.profilFormControl.valueChanges.subscribe(profilValue => {
      this.updateNameFieldsValidation(profilValue);
    });
  }

  private updateNameFieldsValidation(profilValue: string): void {
    if (profilValue === 'CLIENT') {
      // Retirer les validateurs required pour les profils CLIENT
      this.firstNameFormControl.clearValidators();
      this.lastNameFormControl.clearValidators();
    } else {
      // Remettre les validateurs required pour les autres profils
      this.firstNameFormControl.setValidators([Validators.required]);
      this.lastNameFormControl.setValidators([Validators.required]);
    }

    // Actualiser la validation
    this.firstNameFormControl.updateValueAndValidity();
    this.lastNameFormControl.updateValueAndValidity();
  }

  private resetFields() {
    this.email = undefined;
    this.firstNameFormControl.setValue('');
    this.lastNameFormControl.setValue('');
    this.profilFormControl.setValue('');
  }

  private setTrad(): void {
    marker('profile.client');
    marker('profile.invited_vet');
    marker('profile.invited_clinic');
    marker('profile.radiologist');
  }
}

export interface ShareStudyByEmailPopupResult {
  userIds: number[];
  newUsers: UserWithRoleInterface[];
  referingVetIds: number[];
}

export interface UserWithRoleInterface {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}
